import React, { useState, useEffect, useMemo, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { TakeoverLayout } from '_lex/_layouts/TakeoverLayout';
import { Box, Button, Chip, Badge } from '@mui/material';
import { Close as RemoveIcon } from '@mui/icons-material';
import { QuickSearch } from 'components/_atoms';
import { translate as t } from 'hooks/useTranslations';
import { AppContext } from 'views/App/context';
import { DistributableOrganization } from '../_data_sources/useDistributableOrganizations';
import GridSection, { ColumnDef } from 'components/_layouts/GridSection';
import { GridCellParams, GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { neutral } from '_lex/_foundations/phxColors';
import { DTBPackage, DTBPackageHistory } from 'views/App/Portal/DTB/_types/dtb';
import Loading from 'views/App/Portal/Loading';
import _ from 'lodash';
import useDTBDistributionPackageHistory from '../_data_sources/useDTBDistributionPackageHistory';
import datetimeHelper from 'helpers/datetimeHelper';

const DistributePackageHistory: React.FC = () => {
  const { id: dtbPackageId } = useParams<{ id: string }>();
  const { currentOrganizationId } = useContext(AppContext);

  const [dtbPackage, setDTBPackage] = useState<DTBPackage | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [packageHistory, setPackageHistory] = useState<DTBPackageHistory[]>([]);
  const [selectedOrganizations, setSelectedOrganizations] = useState<DistributableOrganization[]>([]);
  const [organizationSearch, setOrganizationSearch] = useState<string>('');
  const [showSelectedOrganizations, setShowSelectedOrganizations] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);

  const packageHistoryQuery = useDTBDistributionPackageHistory(currentOrganizationId || '', dtbPackageId || '');

  // Load package history with pagination
  useEffect(() => {
    if (packageHistoryQuery.data && !packageHistoryQuery.isError) {
      const fetchedHistory =
        packageHistoryQuery.data.pages.flatMap((page: { content: DTBPackageHistory[] }) => page.content) || [];
      setPackageHistory(fetchedHistory);
      setIsLoading(false);
    } else if (packageHistoryQuery.isError) {
      setIsLoading(false);
    }
  }, [packageHistoryQuery.data, packageHistoryQuery.isError]);

  // Update selected organizations when row selection changes
  /*useEffect(() => {
        setSelectedOrganizations(packageHistory.filter((org) => rowSelectionModel.includes(org.id)));
    }, [packageHistory, rowSelectionModel]);*/

  const organizationColumns: ColumnDef = useMemo(
    () => ({
      agency: {
        header: t('dtbs.distribution.packageHistory.columns.agency', 'Agency'),
        flex: 2,
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;
          return <span>{row.agencyName}</span>;
        },
        valueGetter: (value: any, row: any) => {
          return row.agencyName;
        },
      },
      distributionDate: {
        header: t('dtbs.distribution.packageHistory.columns.distributionDate', 'Distribution Date'),
        flex: 2,
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;
          return <span>{datetimeHelper.formatDate(row.distributionDate)}</span>;
        },
        valueGetter: (value: any, row: any) => {
          return datetimeHelper.formatDate(row.distributionDate);
        },
      },
    }),
    [t],
  );

  if (isLoading) {
    return <Loading />;
  }

  return (
    <TakeoverLayout
      pageTitle={`${dtbPackage?.name || 'DTB Package'} - Distribution`}
      backUrl='/admin/dtbs/distribution'
      headerProps={{
        title: t('dtbs.distribution.packageHistory.title'),
      }}
      pageToolbarLeft={
        <QuickSearch
          id='organization_search'
          initialValue={organizationSearch}
          onChange={(val) => setOrganizationSearch(val)}
          placeholder={t('dtbs.distribution.packageHistory.search', 'Search by agency name')}
        />
      }
    >
      <Box sx={{ display: 'grid', gridTemplateRows: 'auto 1fr', gap: 2, minHeight: 0, height: '100%' }}>
        <Box sx={{ px: 4 }}>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'start',
              gap: 3,
            }}
          >
            {selectedOrganizations.length > 3 && (
              <Button
                className='xs'
                variant='outlined'
                size='small'
                onClick={() => setShowSelectedOrganizations(!showSelectedOrganizations)}
                endIcon={
                  showSelectedOrganizations ? null : (
                    <Badge
                      variant='inline'
                      color='primary'
                      badgeContent={selectedOrganizations.length.toString()}
                    />
                  )
                }
              >
                {showSelectedOrganizations
                  ? t('dtbs.distribution.selectOrganizations.showLess', 'Show Less')
                  : t('dtbs.distribution.selectOrganizations.showAll', 'Show All')}
              </Button>
            )}
            {selectedOrganizations.length > 0 && (
              <>
                <Button
                  className='xs'
                  variant='text'
                  size='small'
                  onClick={() => setRowSelectionModel([])}
                >
                  {t('dtbs.distribution.selectOrganizations.clearAll', 'Clear All')}
                </Button>
                <Box
                  sx={{
                    height: '2rem',
                    border: `solid 1px ${neutral[300]}`,
                  }}
                ></Box>
              </>
            )}
            {_.take(selectedOrganizations, showSelectedOrganizations ? selectedOrganizations.length : 3).map((org) => (
              <Chip
                key={`selected_org_${org.id}`}
                label={org.name}
                onDelete={() => setRowSelectionModel((prev) => prev.filter((x) => x !== org.id))}
                deleteIcon={<RemoveIcon />}
                size='small'
                sx={{
                  justifyContent: 'space-between',
                  gap: 2,
                  height: '2rem',
                  minHeight: '2rem',
                  backgroundColor: neutral[50],
                  '& .MuiChip-label': {
                    lineHeight: '2rem',
                    padding: '0 8px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  },
                }}
              />
            ))}
          </Box>
        </Box>
        <GridSection
          columns={organizationColumns}
          data={packageHistory}
          query={packageHistoryQuery}
          totalCount={packageHistory.length}
          gridProps={{
            getRowId: (row: any) => `${row.packageName}-${row.distributionDate}-${row.agencyName}`,
            filterModel: {
              items: [{ field: 'agencyName', operator: 'contains', value: organizationSearch }],
            },
          }}
        />
      </Box>
    </TakeoverLayout>
  );
};

export default DistributePackageHistory;
